import type { OidcTokenInfo } from "@/lib/authSession";
import {
  HUMHUB_MAX_REFRESH_BUFFER_MS,
  HUMHUB_MIN_REFRESH_BUFFER_MS,
  MIN_SESSION_REFRESH_DELAY_MS,
  OIDC_MAX_REFRESH_BUFFER_MS,
  OIDC_MIN_REFRESH_BUFFER_MS,
} from "@/lib/authSessionConfig";
export interface AuthSessionSnapshot {
  isAuthenticated: boolean;
  accessToken?: string;
  accessTokenExpiresAt?: number;
  isAccessTokenExpired: boolean;
}

export const buildAuthSessionSnapshot = (auth: {
  isAuthenticated: boolean;
  user?: {
    access_token?: string;
    expires_at?: number;
    expired?: boolean;
  } | null;
}): AuthSessionSnapshot => ({
  isAuthenticated: auth.isAuthenticated,
  accessToken: auth.user?.access_token,
  accessTokenExpiresAt:
    typeof auth.user?.expires_at === "number"
      ? auth.user.expires_at * 1000
      : undefined,
  isAccessTokenExpired: Boolean(auth.user?.expired),
});

export const buildOidcTokenInfo = (
  accessToken?: string,
  expiresAt?: number,
): OidcTokenInfo | null => {
  if (!accessToken) {
    return null;
  }

  return { token: accessToken, expiresAt };
};

export const selectPreferredOidcToken = (
  authToken: OidcTokenInfo | null,
  latestToken: OidcTokenInfo | null,
) => {
  if (!authToken) {
    return latestToken;
  }

  if (!latestToken) {
    return authToken;
  }

  const authExpiresAt = authToken.expiresAt ?? 0;
  const latestExpiresAt = latestToken.expiresAt ?? 0;

  return latestExpiresAt > authExpiresAt ? latestToken : authToken;
};

export const getPreferredOidcToken = (
  snapshot: AuthSessionSnapshot,
  latestToken: OidcTokenInfo | null,
) => {
  const authToken =
    snapshot.isAuthenticated && !snapshot.isAccessTokenExpired
      ? buildOidcTokenInfo(snapshot.accessToken, snapshot.accessTokenExpiresAt)
      : null;

  return selectPreferredOidcToken(authToken, latestToken);
};

export const getOidcTokenCandidates = (
  snapshot: AuthSessionSnapshot,
  latestToken: OidcTokenInfo | null,
) => {
  const candidates: OidcTokenInfo[] = [];
  const authToken =
    snapshot.isAuthenticated && !snapshot.isAccessTokenExpired
      ? buildOidcTokenInfo(snapshot.accessToken, snapshot.accessTokenExpiresAt)
      : null;

  if (authToken) {
    candidates.push(authToken);
  }

  const preferredToken = getPreferredOidcToken(snapshot, latestToken);
  if (
    preferredToken &&
    !candidates.some((candidate) => candidate.token === preferredToken.token)
  ) {
    candidates.push(preferredToken);
  }

  return candidates;
};

export const getAdaptiveRefreshBufferMs = (
  expiresAt: number | undefined,
  {
    maxBufferMs,
    minBufferMs,
    now = Date.now(),
  }: {
    maxBufferMs: number;
    minBufferMs: number;
    now?: number;
  },
) => {
  if (typeof expiresAt !== "number") {
    return undefined;
  }

  const remainingMs = expiresAt - now;
  if (remainingMs <= 0) {
    return minBufferMs;
  }

  return Math.min(
    maxBufferMs,
    Math.max(minBufferMs, Math.floor(remainingMs * 0.1)),
  );
};

export const isExpiredOrWithinBuffer = (
  expiresAt?: number,
  bufferMs = 0,
  now = Date.now(),
) => {
  if (!expiresAt) {
    return false;
  }

  return now >= expiresAt - bufferMs;
};

export const getNextSessionRefreshAt = ({
  oidcToken,
  humhubExpiresAt,
  now = Date.now(),
}: {
  oidcToken: OidcTokenInfo | null;
  humhubExpiresAt?: number;
  now?: number;
}) => {
  const oidcRefreshAt = (() => {
    const oidcBufferMs = getAdaptiveRefreshBufferMs(oidcToken?.expiresAt, {
      maxBufferMs: OIDC_MAX_REFRESH_BUFFER_MS,
      minBufferMs: OIDC_MIN_REFRESH_BUFFER_MS,
      now,
    });
    if (
      typeof oidcToken?.expiresAt !== "number" ||
      typeof oidcBufferMs !== "number"
    ) {
      return undefined;
    }

    return oidcToken.expiresAt - oidcBufferMs;
  })();

  const humhubRefreshAt = (() => {
    const humhubBufferMs = getAdaptiveRefreshBufferMs(humhubExpiresAt, {
      maxBufferMs: HUMHUB_MAX_REFRESH_BUFFER_MS,
      minBufferMs: HUMHUB_MIN_REFRESH_BUFFER_MS,
      now,
    });
    if (
      typeof humhubExpiresAt !== "number" ||
      typeof humhubBufferMs !== "number"
    ) {
      return undefined;
    }

    return humhubExpiresAt - humhubBufferMs;
  })();

  const candidates = [oidcRefreshAt, humhubRefreshAt].filter(
    (value): value is number => typeof value === "number",
  );

  if (candidates.length === 0) {
    return undefined;
  }

  return Math.min(...candidates);
};

export const calculateNextSessionRefreshDelay = ({
  oidcToken,
  humhubExpiresAt,
  now = Date.now(),
}: {
  oidcToken: OidcTokenInfo | null;
  humhubExpiresAt?: number;
  now?: number;
}) => {
  const nextRefreshAt = getNextSessionRefreshAt({
    oidcToken,
    humhubExpiresAt,
    now,
  });

  if (typeof nextRefreshAt !== "number") {
    return undefined;
  }

  return Math.max(nextRefreshAt - now, MIN_SESSION_REFRESH_DELAY_MS);
};
